import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FileSelect, FileSelectProps } from '../../../../../../shared/Components/FileSelect';
import { ErrorMessage } from '../../../../../../shared/Typhography';
import { FileState } from '../../types';
import { VALID_TYPE_STATES } from '../../constants';
import { fileToObjectUrl } from '../../../../../../services/media';
import { getImageResolution } from '../../utilities';

interface FileUploadProps {
  hasFileRender: (state?: FileState, removeFile?: () => void, disabled?: boolean) => ReactNode;
  noFileRender: (state?: FileState) => ReactNode;
  fileUploadProps?: Omit<FileSelectProps, 'onChange' | 'multiple' | 'children'>;
  onFileAddedHook?: (state?: FileState, file?: File) => void;
  initialState?: Partial<FileState>;
  isEditDisabled?: boolean;
  minimumResolution?: { width: number; height: number };
}

export const FileUpload = ({
  hasFileRender,
  noFileRender,
  fileUploadProps,
  initialState,
  onFileAddedHook,
  isEditDisabled,
  minimumResolution,
}: FileUploadProps) => {
  const [fileState, setFileState] = useState<FileState | undefined>(
    initialState ? (initialState as FileState) : undefined,
  );
  const fileRef = useRef<File | undefined>();
  const hasFile = useMemo(
    () => fileState?.state && VALID_TYPE_STATES.includes(fileState?.state),
    [fileState],
  );

  const removeFile = useCallback(() => {
    setFileState(undefined);
    if (fileRef.current) {
      fileRef.current = undefined;
      onFileAddedHook && onFileAddedHook(undefined, undefined);
    }
  }, [fileState, onFileAddedHook]);

  const onFileAddedHandler = useCallback(
    (file: File) => {
      try {
        fileRef.current = file;
        setFileState({
          state: 'added',
          url: fileToObjectUrl(file),
          fileType: file.type,
        });

        onFileAddedHook &&
          onFileAddedHook(
            {
              state: 'added',
              url: fileToObjectUrl(file),
              fileType: file.type,
            },
            fileRef.current,
          );
      } catch (err) {
        setFileState({
          url: '',
          errorMessage: `${(err as Error)?.message}`,
          state: 'error',
        });
      }
    },
    [fileState, onFileAddedHook],
  );

  const checkResolution = useCallback(async () => {
    if (fileState?.state !== 'added' || !minimumResolution) {
      return;
    }

    const fileResolution = await getImageResolution(fileState.url);
    if (
      fileResolution.width < minimumResolution?.width ||
      fileResolution.height < minimumResolution.height
    ) {
      setFileState({
        ...fileState,
        errorMessage: `Minimum resolution of ${minimumResolution.width}x${minimumResolution.height} is not satesfied!`,
      });
    }
  }, [minimumResolution, fileState]);

  useEffect(() => {
    checkResolution();
  }, [checkResolution, fileState]);

  return (
    <>
      {hasFile && hasFileRender(fileState, removeFile, isEditDisabled)}
      {!hasFile && (
        <FileSelect
          onChange={(event) => {
            if (!event.currentTarget.files || event.currentTarget.files.length <= 0) return;

            const addedFile = event.currentTarget.files?.item(0);
            if (!addedFile) return;

            if (fileUploadProps?.accept) {
              const splitAcceptType = fileUploadProps?.accept.split(',');
              if (!splitAcceptType?.includes(addedFile.type)) {
                const acceptedTypesItems = splitAcceptType
                  .map((acceptType) => acceptType.split('/').at(-1))
                  .join(', ');

                setFileState({
                  ...fileState,
                  state: 'error',
                  errorMessage: `You can add only following types: ${acceptedTypesItems}`,
                  url: '',
                });
                return;
              }
            }

            onFileAddedHandler(addedFile);
          }}
          {...fileUploadProps}
          multiple={false}
        >
          {noFileRender && noFileRender(fileState)}

          {fileState?.state === 'error' && (
            <ErrorMessage style={{ position: 'relative', left: 0, bottom: 0 }}>
              {fileState?.errorMessage}
            </ErrorMessage>
          )}
        </FileSelect>
      )}
    </>
  );
};
